import { defineStore } from 'pinia';
import { reactive, ref, toRaw, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import type { WebSocketConnection, WebSocketMessage, MaintenanceMessage } from '@/types/sockets';
import type { TopPlayer } from '@/types';
import { useSiteStatusStore } from './siteStatus';
import { useBonusesStore } from './bonuses';
import { useNewPlayersStore } from './newPlayers';
import { useLimitsStore } from './limits';
import { useTournamentsStore } from './tournaments';
import { useAuthStore } from './auth';
import { useLevelsStore } from './levels';
import { useNotificationsStore } from './notifications';
import { usePlayerStore } from './player';

export const useWebSocketStore = defineStore('websocket', () => {
    const router = useRouter();
    const toast = useToast();
    const connections = reactive<Record<string, WebSocketConnection>>({});
    const siteStatus = useSiteStatusStore();
    const bonusesStore = useBonusesStore();
    const newPlayers = useNewPlayersStore();
    const limitsStore = useLimitsStore();
    const tournamentsStore = useTournamentsStore();
    const {
        toSetTournamentOngoingTrigger,
        toSetNewTournament,
        toBindTournament,
        toUpdateTournament,
        awaitGetAvailableTournaments,
        awaitGetMyTournaments,
        awaitGetCompletedTournament,
        availableTournaments,
        myTournaments,
        tournament,
        completedTournament
    } = tournamentsStore;
    const authStore = useAuthStore();
    const levelsStore = useLevelsStore();
    const topPlayers = ref<TopPlayer[]>([]);
    const notificationsStore = useNotificationsStore();
    const playerStore = usePlayerStore();
    // Check if WebSocket is connected
    const isConnected = (key: string) => {
        return connections[key]?.isConnected || false;
    };

    // handleMessage
    const handleMessage = async (key: string, message: MessageEvent): Promise<void> => {
        try {
            const parsedMessage: WebSocketMessage = JSON.parse(message.data);
            const { data, type } = parsedMessage;
            // console.log(`[${key}] Message received:`, parsedMessage);

            switch (type) {
                case 'new_player':
                    // console.log(`[${key}] New player:`, parsedMessage);
                    newPlayers.toAddNewPlayer(data);
                    break;
                case 'top_players':
                    // console.log(`[${key}] Top players:`, parsedMessage);
                    topPlayers.value = data;
                    break;
                case 'notification': {
                    // console.log(`[${key}] Message received:`, parsedMessage);
                    notificationsStore.toAddNotification(data);
                    toast.add({
                        severity: 'info',
                        group: 'global',
                        summary: 'new_message',
                        detail: 'check_new_message',
                        life: 4000
                    });
                    break;
                }
                case 'limit_reached': {
                    const limitData = {
                        modal: true,
                        value: data.limit_value,
                        start_date: data.limit_start_date,
                        end_date: data.limit_end_date,
                        usage: data.limit_usage
                    };
                    if (data.limit_type === 'deposit') {
                        limitsStore.toSetLimitsData(limitData, 'depositFlag');
                    } else if (data.limit_type === 'loss') {
                        limitsStore.toSetLimitsData(limitData, 'lossFlag');
                    } else if (data.limit_type === 'wagering') {
                        limitsStore.toSetLimitsData(limitData, 'wageringFlag');
                    }
                    break;
                }
                case 'new_balance':
                    // console.log(`[${key}] New balance:`, parsedMessage);
                    playerStore.toUpdatePlayerBalance(data);
                    break;
                case 'user_blocked':
                    authStore.toSetBanInfo(data, true);
                    break;
                case 'maintenance':
                    // console.log('maintenance', parsedMessage);
                    handleMaintenanceMessage(data);
                    break;
                case 'new_level':
                    levelsStore.toUpNextLevel();
                    break;
                case 'new_bonus':
                    bonusesStore.getBonusesWithRequestGuard(data.bonus_to_user_id);
                    break;
                case 'tournament': {
                    // console.log('tournament', parsedMessage);
                    const tournamentMessage = data;
                    if (tournamentMessage.status === 'created') {
                        if (availableTournaments.items.length) {
                            awaitGetAvailableTournaments(tournamentMessage.id);
                        }
                        toSetNewTournament(false, null);
                        nextTick(() => {
                            toSetNewTournament(true, null, tournamentMessage.id);
                        });
                    } else if (tournamentMessage.status === 'ongoing') {
                        toSetTournamentOngoingTrigger(true, tournamentMessage);
                        toUpdateTournament(tournamentMessage);
                    } else if (tournamentMessage.status === 'completed') {
                        if (!tournamentMessage.is_user_auto_subscribe_enabled && availableTournaments.items.length) {
                            awaitGetAvailableTournaments();
                        }
                        if (tournamentMessage.is_user_auto_subscribe_enabled && myTournaments.items.length) {
                            awaitGetMyTournaments();
                        }
                        if (tournament?.id !== tournamentMessage.id) {
                            awaitGetCompletedTournament(tournamentMessage.id);
                        } else if (tournament) {
                            toBindTournament(tournament, completedTournament);
                        }
                    }
                    break;
                }
                default:
                    console.error(`[${key}] Unknown message type:`, parsedMessage);
            }
        } catch (error) {
            console.error(`[${key}] Error parsing message:`, error);
        }

        // maintenance
        function handleMaintenanceMessage(message: MaintenanceMessage): void {
            if (message.status) {
                // console.log(`[maintenance] Handling maintenance message:`, message.status);
                siteStatus.toSetSiteStatus('maintenance', true);
                router.push('/maintenance');
            } else if (siteStatus.isMaintenance) {
                siteStatus.toSetSiteStatus('maintenance', false);
                // router.push('/');
                location.reload();
            }
        }
    };

    // initialize WebSocket
    const initWebSocket = (key: string, url: string): void => {
        const rawConnections = toRaw(connections);
        if (rawConnections[key]) {
            closeWebSocket(key);
            // console.warn(`WebSocket with key "${key}" is already connected`);
            return;
        }

        const socket = new WebSocket(url);

        const connection: WebSocketConnection = {
            socket,
            isConnected: false
        };

        connections[key] = connection;

        socket.onopen = (/*event: Event*/): void => {
            // console.log(`WebSocket [${key}] connected`, event);
            connection.isConnected = true;

            const token = localStorage.getItem('jwt');
            if (token) {
                socket.send(JSON.stringify({ token }));
            }
        };

        socket.onmessage = (event: MessageEvent) => handleMessage(key, event);

        socket.onclose = (): void => {
            console.log(`WebSocket [${key}] closed`);
            connection.isConnected = false;
            delete connections[key];
        };

        socket.onerror = (error: Event): void => {
            console.error(`WebSocket [${key}] error:`, error);
        };
    };

    // method for sending messages
    const sendMessage = (key: string, message: WebSocketMessage): void => {
        const connection = connections[key];
        if (connection && connection.socket.readyState === WebSocket.OPEN) {
            connection.socket.send(JSON.stringify(message));
        } else {
            console.error(`WebSocket [${key}] is not connected`);
        }
    };
    const sendToken = (key: string): void => {
        const connection = connections[key];
        const token = localStorage.getItem('jwt');
        if (connection && connection.socket.readyState === WebSocket.OPEN && token) {
            connection.socket.send(JSON.stringify({ token }));
        }
    };
    // close WebSocket
    const closeWebSocket = (key: string): void => {
        const connection = connections[key];
        if (connection) {
            connection.socket.close();
            delete connections[key];
        } else {
            console.warn(`WebSocket [${key}] does not exist`);
        }
    };

    // close all WebSockets
    const closeAllWebSockets = (): void => {
        Object.keys(connections).forEach((key) => {
            closeWebSocket(key);
        });
    };

    return {
        connections,
        isConnected,
        initWebSocket,
        sendMessage,
        sendToken,
        closeWebSocket,
        closeAllWebSockets
    };
});
